import { DrivingLicenceCheck } from './driving-licence-check';
import { Loading } from 'components/loading';
import { InlineSvg } from 'components/svg';
import { UpdateEmployeePersonalDetailsInput } from 'features/employees/api/repositories/use-employee-personal-details-update.mutation';
import { genders, maritalStatuses } from 'features/shared/enums';
import 'features/shared/styles.css';
import moment from 'moment';
import { FC } from 'react';
import Flatpickr from 'react-flatpickr';
import {
  Control,
  FieldErrors,
  FieldNamesMarkedBoolean,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch
} from 'react-hook-form';
import { formatDate } from 'utils/formats';

type Props = {
  isLoading: boolean;
  isSubmitting: boolean;
  register: UseFormRegister<UpdateEmployeePersonalDetailsInput>;
  control: Control<UpdateEmployeePersonalDetailsInput>;
  getValues: UseFormGetValues<UpdateEmployeePersonalDetailsInput>;
  setValue: UseFormSetValue<UpdateEmployeePersonalDetailsInput>;
  trigger: UseFormTrigger<UpdateEmployeePersonalDetailsInput>;
  errors: FieldErrors<UpdateEmployeePersonalDetailsInput>;
  touchedFields: Partial<Readonly<FieldNamesMarkedBoolean<UpdateEmployeePersonalDetailsInput>>>;
  watch: UseFormWatch<UpdateEmployeePersonalDetailsInput>;
};

const PersonalDetails: FC<Props> = ({
  isLoading,
  isSubmitting,
  control,
  getValues,
  setValue,
  register,
  trigger,
  errors,
  touchedFields,
  watch
}) => {
  return (
    <div className="card mb-5 mb-xl-10">
      <div
        className="card-header border-0 cursor-pointer"
        role="button"
        data-bs-toggle="collapse"
        data-bs-target="#kt_employee_personal_details"
        aria-expanded="true"
        aria-controls="kt_employee_personal_details"
      >
        <div className="card-title m-0">
          <h3 className="fw-bolder m-0">Personal Details</h3>
        </div>
      </div>

      <div id="kt_form_employee_personal_details" className="collapse show">
        <div className="card-body border-top p-9">
          <div className="row mb-6">
            <div className="col-lg-6 fv-row">
              <label className="col-form-label fs-6">Surname</label>
              <input
                type="text"
                disabled={isSubmitting || isLoading}
                className="form-control form-control-lg form-control-solid"
                placeholder="Surname"
                {...register('input.requestBody.surname')}
              />
              {touchedFields.input?.requestBody?.surname && errors.input?.requestBody?.surname && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{errors.input.requestBody.surname?.message}</div>
                </div>
              )}
            </div>
            <div className="col-lg-6 fv-row">
              <div className="row">
                <div className="col-lg-6">
                  <label className="col-form-label fs-6">Male / Female</label>
                  <select
                    className="form-select form-select-solid form-select-lg"
                    disabled={isSubmitting || isLoading}
                    {...register('input.requestBody.gender')}
                  >
                    <option value="">Select a gender..</option>
                    {genders.map((item, idx) => (
                      <option value={item.value} key={idx}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  {touchedFields.input?.requestBody?.gender && errors.input?.requestBody?.gender && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">{errors.input.requestBody.gender?.message}</div>
                    </div>
                  )}
                </div>
                <div className="col-lg-6">
                  <label className="col-form-label required fs-6">Marital Status</label>
                  <select
                    className="form-select form-select-solid form-select-lg"
                    disabled={isSubmitting || isLoading}
                    {...register('input.requestBody.maritalStatus')}
                  >
                    <option value="">Select a martial status..</option>
                    {maritalStatuses.map((item, idx) => (
                      <option value={item.value} key={idx}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  {touchedFields.input?.requestBody?.maritalStatus && errors.input?.requestBody?.maritalStatus && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">{errors.input.requestBody.maritalStatus?.message}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="row mb-6">
            <div className="col-lg-6 fv-row">
              <label className="col-form-label fs-6">Forenames</label>
              <input
                type="text"
                disabled={isSubmitting || isLoading}
                className="form-control form-control-lg form-control-solid"
                placeholder="Patronymic"
                {...register('input.requestBody.patronymic')}
              />
              {touchedFields.input?.requestBody?.patronymic && errors.input?.requestBody?.patronymic && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{errors.input.requestBody.patronymic?.message}</div>
                </div>
              )}
            </div>
            <div className="col-lg-6 fv-row">
              <label className="col-form-label fs-6">Next of kin / relationship</label>
              <input
                type="text"
                disabled={isSubmitting || isLoading}
                className="form-control form-control-lg form-control-solid"
                placeholder="Next of kin or relationship"
                {...register('input.requestBody.nextOfKinOrRelationship')}
              />
              {touchedFields.input?.requestBody?.nextOfKinOrRelationship &&
                errors.input?.requestBody?.nextOfKinOrRelationship && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{errors.input.requestBody.nextOfKinOrRelationship?.message}</div>
                  </div>
                )}
            </div>
          </div>
          <div className="row mb-6">
            <div className="col-lg-6 fv-row">
              <label className="col-form-label fs-6">Home Address</label>
              <input
                type="text"
                disabled={isSubmitting || isLoading}
                className="form-control form-control-lg form-control-solid"
                placeholder="Home address"
                {...register('input.requestBody.homeAddress')}
              />
              {touchedFields.input?.requestBody?.homeAddress && errors.input?.requestBody?.homeAddress && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{errors.input.requestBody.homeAddress?.message}</div>
                </div>
              )}
            </div>
            <div className="col-lg-6 fv-row">
              <label className="col-form-label fs-6">Next of kin&apos;s day time No</label>
              <input
                type="text"
                disabled={isSubmitting || isLoading}
                className="form-control form-control-lg form-control-solid"
                placeholder="Next of kins day time no"
                {...register('input.requestBody.nextOfKinsDayTimeNo')}
              />
              {touchedFields.input?.requestBody?.nextOfKinsDayTimeNo &&
                errors.input?.requestBody?.nextOfKinsDayTimeNo && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{errors.input.requestBody.nextOfKinsDayTimeNo?.message}</div>
                  </div>
                )}
            </div>
          </div>
          <div className="row mb-6">
            <div className="col-lg-6 fv-row">
              <label className="col-form-label fs-6">Post Code</label>
              <input
                type="text"
                disabled={isSubmitting || isLoading}
                className="form-control form-control-lg form-control-solid"
                placeholder="Post Code"
                {...register('input.requestBody.postCode')}
              />
              {touchedFields.input?.requestBody?.postCode && errors.input?.requestBody?.postCode && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{errors.input.requestBody.postCode?.message}</div>
                </div>
              )}
            </div>

            <div className="col-lg-6 fv-row">
              <label className="col-form-label fs-6">Nationality</label>
              <input
                type="text"
                disabled={isSubmitting || isLoading}
                className="form-control form-control-lg form-control-solid"
                placeholder="Nationality"
                {...register('input.requestBody.nationality')}
              />
              {touchedFields.input?.requestBody?.nationality && errors.input?.requestBody?.nationality && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{errors.input.requestBody.nationality?.message}</div>
                </div>
              )}
            </div>
          </div>
          <div className="row mb-6">
            <div className="col-lg-6 fv-row">
              <label className="col-form-label fs-6">Personal Email</label>
              <input
                type="text"
                disabled={isSubmitting || isLoading}
                className="form-control form-control-lg form-control-solid"
                placeholder="Personal email"
                {...register('input.requestBody.personalEmail')}
              />
              {touchedFields.input?.requestBody?.personalEmail && errors.input?.requestBody?.personalEmail && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{errors.input.requestBody.personalEmail?.message}</div>
                </div>
              )}
            </div>
            <div className="col-lg-6 fv-row">
              <label className="col-form-label fs-6">National Ins No</label>
              <input
                type="text"
                disabled={isSubmitting || isLoading}
                className="form-control form-control-lg form-control-solid"
                placeholder="National ins No"
                {...register('input.requestBody.nationalInsNo')}
              />
              {touchedFields.input?.requestBody?.nationalInsNo && errors.input?.requestBody?.nationalInsNo && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{errors.input.requestBody.nationalInsNo?.message}</div>
                </div>
              )}
            </div>
          </div>
          <div className="row mb-6">
            <div className="row">
              <div className="col-lg-6">
                <label className="col-form-label fs-6">Home Phone Number</label>
                <input
                  type="number"
                  disabled={isSubmitting || isLoading}
                  className="form-control form-control-lg form-control-solid"
                  placeholder="Home phone number"
                  {...register('input.requestBody.homePhoneNumber')}
                />
                {touchedFields.input?.requestBody?.homePhoneNumber && errors.input?.requestBody?.homePhoneNumber && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{errors.input.requestBody.homePhoneNumber?.message}</div>
                  </div>
                )}
              </div>
              <div className="col-lg-6">
                <label className="col-form-label fs-6">Mobile Phone Number</label>
                <input
                  type="number"
                  disabled={isSubmitting || isLoading}
                  className="form-control form-control-lg form-control-solid"
                  placeholder="Mobile phone number"
                  {...register('input.requestBody.mobilePhoneNumber')}
                />
                {touchedFields.input?.requestBody?.mobilePhoneNumber &&
                  errors.input?.requestBody?.mobilePhoneNumber && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">{errors.input.requestBody.mobilePhoneNumber?.message}</div>
                    </div>
                  )}
              </div>
            </div>
          </div>
          <div className="row mb-6">
            <div
              className="col-lg-6 fv-row"
              style={{
                position: 'relative'
              }}
            >
              <div className="d-flex flex-column">
                <label className="col-form-label fs-6">Day of Birth</label>
                <Flatpickr
                  placeholder={'Birth date'}
                  className="form-control form-control-lg form-control-solid"
                  value={
                    getValues('input.requestBody.birthDate')
                      ? moment(getValues('input.requestBody.birthDate'), 'YYYY-MM-DD').toDate()
                      : undefined
                  }
                  onChange={([date]) => {
                    setValue('input.requestBody.birthDate', date ? formatDate(date, 'YYYY-MM-DD') : date);
                    trigger();
                  }}
                  options={{
                    dateFormat: 'd/m/Y',
                    static: true,
                    locale: {
                      firstDayOfWeek: 1
                    },
                    allowInput: true
                  }}
                  disabled={isSubmitting || isLoading}
                />
                <div className="av-calendar-icon">
                  <i className="ki-duotone ki-calendar fs-2">
                    <span className="path1"></span>
                    <span className="path2"></span>
                  </i>
                </div>
                {touchedFields.input?.requestBody?.birthDate && errors.input?.requestBody?.birthDate && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{errors.input.requestBody.birthDate?.message}</div>
                  </div>
                )}
              </div>
            </div>
            <div
              className="col-lg-6 fv-row"
              style={{
                position: 'relative'
              }}
            >
              <div className="d-flex flex-column">
                <label className="col-form-label fs-6">Date Commenced</label>
                <Flatpickr
                  placeholder={'Commenced date'}
                  className="form-control form-control-lg form-control-solid"
                  value={
                    getValues('input.requestBody.commencedDate')
                      ? moment(getValues('input.requestBody.commencedDate'), 'YYYY-MM-DD').toDate()
                      : undefined
                  }
                  onChange={([date]) => {
                    setValue('input.requestBody.commencedDate', date ? formatDate(date, 'YYYY-MM-DD') : date);
                    trigger();
                  }}
                  options={{
                    dateFormat: 'd/m/Y',
                    static: true,
                    locale: {
                      firstDayOfWeek: 1
                    }
                  }}
                  disabled={isSubmitting || isLoading}
                />
                <div className="av-calendar-icon">
                  <i className="ki-duotone ki-calendar fs-2">
                    <span className="path1"></span>
                    <span className="path2"></span>
                  </i>
                </div>
                {touchedFields.input?.requestBody?.commencedDate && errors.input?.requestBody?.commencedDate && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{errors.input.requestBody.commencedDate?.message}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="row mb-6">
            <div className="col-lg-6 fv-row">
              <label className="col-form-label fs-6">Driving Licence Number</label>
              <input
                type="text"
                disabled={isSubmitting || isLoading}
                className="form-control form-control-lg form-control-solid"
                placeholder="Driving Licence Number"
                {...register('input.requestBody.drivingLicenseNumber')}
              />
              {touchedFields.input?.requestBody?.drivingLicenseNumber &&
                errors.input?.requestBody?.drivingLicenseNumber && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{errors.input.requestBody.drivingLicenseNumber?.message}</div>
                  </div>
                )}
            </div>
            <div className="col-lg-6 fv-row">
              <label className="col-form-label fs-6">Penalties & Disqualifications</label>
              <input
                type="text"
                maxLength={256}
                disabled={isSubmitting || isLoading}
                className="form-control form-control-lg form-control-solid"
                placeholder="Penalties & Disqualifications"
                {...register('input.requestBody.penaltiesAndDisqualifications')}
              />
              {errors.input?.requestBody?.penaltiesAndDisqualifications && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{errors.input.requestBody.penaltiesAndDisqualifications?.message}</div>
                </div>
              )}
            </div>
          </div>

          <div className="row mb-6">
            <div className="col-lg-6 fv-row">
              <input
                className="form-check-input"
                type="checkbox"
                disabled={isSubmitting || isLoading}
                {...register('input.requestBody.hasPermissionDriveCompanyCar')}
              />
              <label className="form-form-label ps-2 fs-6" htmlFor="deactivate">
                Permission to drive company car vehicle
              </label>
            </div>
            <div
              className="col-lg-6"
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <DrivingLicenceCheck setValue={setValue} getValues={getValues} watch={watch} />
            </div>
          </div>
        </div>
      </div>
      {isLoading && <Loading />}
    </div>
  );
};

export { PersonalDetails };
